import React, { useCallback, useEffect, useState } from 'react';
import { IntegrationFlow } from '@sunbit-dev/web-ui-lib';
import { AppContext } from '../contexts/appContext';
import SunbitAPI from '../services/SunbitApi';
import { LoaderActionContext } from '../contexts/loaderContext';
import { CompanyUrls } from '../constants/appConstants';
import {
  IMembersData,
  IAllianceData,
  TestWebhooksPopupType,
  IWebhook,
  IEnvironmentStatus,
  Environments,
  Ikeys,
} from '../constants/models';
import mixpanel from 'mixpanel-browser';

const useEnvironment = ({ token, userName }: { token: string; userName: string }) => {
  const [environments, setEnvironments] = useState([] as IEnvironmentStatus[]);
  const [waiting, setIsWaiting] = useState(false);

  useEffect(() => {
    const init = async () => {
      setIsWaiting(true);
      try {
        const data = await SunbitAPI.getRequestProdStatus({ eventName: 'Fetch Environments', props: { userName } });
        setEnvironments(data?.environments);
      } catch (e) {
        console.error('Error - Fetch environments');
      } finally {
        setIsWaiting(false);
      }
    };

    token && init();
  }, [token, userName]);

  const onRequestProdAccess = useCallback(async () => {
    try {
      setIsWaiting(true);

      const response = await SunbitAPI.requestProdAccess({ eventName: 'Request Prod Access', props: { userName } });
      const newEnvs = environments?.map((e) => {
        if (e.name === response.name) return response;
        return e;
      });

      setEnvironments(newEnvs);
    } catch (e) {
      console.error('Error - Request Prod Access');
    } finally {
      setIsWaiting(false);
    }
  }, [environments, userName]);

  const prodRequestStatus = environments?.find((e) => e.name === Environments.prod)?.status;

  return { onRequestProdAccess, prodRequestStatus, waiting };
};

const useKeys = ({ token }: { token: string }) => {
  const [keys, setKeys] = useState([] as Ikeys[]);
  const [waiting, setIsWaiting] = useState(true);

  const fetch = async () => {
    try {
      setIsWaiting(true);
      const userKeysResponse = await SunbitAPI.getUserKeys({ eventName: 'Fetch user keys', props: { token } });

      const keys = (userKeysResponse?.keys || []).map(({ name, value, ...rest }) => ({
        sunbitKey: name,
        secretKey: value,
        ...rest,
      }));
      setKeys(keys);
    } catch (e) {
      console.error('Error fetching keys');
    } finally {
      setIsWaiting(false);
    }
  };

  useEffect(() => {
    token && fetch();
    // eslint-disable-next-line
  }, [token]);

  return { keys, fetch, waiting };
};

const useWebhooks = ({
  token,
  setTestWebhookPopup,
  userName,
}: {
  token: string;
  setTestWebhookPopup: (popupStatus: string) => void;
  userName: string;
}) => {
  const [webhooks, setWebhooks] = useState([] as IWebhook[]);
  const [waiting, setWaiting] = useState(true);

  const fetch = async () => {
    setWaiting(true);
    try {
      const webhooksData = await SunbitAPI.getUserOrgWebhookData({ eventName: 'Fetch webhooks', props: { token } });
      setWebhooks(webhooksData?.webhooks);
    } catch (e) {
      console.error('Error fetching keys');
    } finally {
      setWaiting(false);
    }
  };

  useEffect(() => {
    token && fetch();
    // eslint-disable-next-line
  }, [token]);

  const setWebhook = useCallback(
    (webhook: IWebhook) => {
      const newWebhooks = webhooks.map((w) => {
        if (webhook.environment === w.environment) return webhook;

        return w;
      });

      setWebhooks(newWebhooks);
    },
    [webhooks]
  );

  const testWebhookUrl = useCallback(
    async (url: string) => {
      const key = webhooks?.find((w) => w.url === url)?.key || '';

      try {
        setWaiting(true);

        await SunbitAPI.testWebhookUrl(url, key, {
          eventName: 'Test webhook',
          props: { url, webhookSecret: key, userName },
        });
        setTestWebhookPopup(TestWebhooksPopupType.testWebhookSuccess);
      } catch (error) {
        setTestWebhookPopup(TestWebhooksPopupType.testWebhookFail);
      } finally {
        setWaiting(false);
      }
    },
    [webhooks, setTestWebhookPopup, userName]
  );

  const updateWebhook = useCallback(
    async (environment: Environments) => {
      try {
        setWaiting(true);

        const webhook = webhooks.find((w) => w.environment === environment) as IWebhook;
        const newWebhook = await SunbitAPI.updateWebhookUrl(webhook?.url, webhook?.environment, {
          eventName: 'Update webhook',
          props: {
            webhookUrl: webhook.url,
            webhookSecret: webhook.key,
            userName,
          },
        });

        setWebhook(newWebhook);
      } catch (error) {
        setTestWebhookPopup(TestWebhooksPopupType.testWebhookFail);
      } finally {
        setWaiting(false);
      }
    },
    [webhooks, setTestWebhookPopup, setWebhook, userName]
  );

  return { webhooks, fetch, waiting, setWebhook, updateWebhook, testWebhookUrl };
};

const MainLayoutContainer = ({ logout }: { logout: () => void }) => {
  const appContext = React.useContext(AppContext);
  const loaderContext = React.useContext(LoaderActionContext);
  const {
    credentials: { userToken, userName },
    setTestWebhookPopup,
  } = appContext;

  const {
    onRequestProdAccess,
    prodRequestStatus,
    waiting: environmentsWaiting,
  } = useEnvironment({ token: userToken, userName });

  const { keys, waiting: keysWaiting } = useKeys({ token: userToken });

  const {
    webhooks,
    waiting: webhooksWaiting,
    setWebhook,
    testWebhookUrl,
    updateWebhook,
  } = useWebhooks({ token: userToken, setTestWebhookPopup, userName });

  // Get all user details after login
  React.useEffect(() => {
    const getUserDetails = async () => {
      try {
        const alliancePromise = SunbitAPI.getAllianceData({ eventName: 'Fetch alliance data', props: { userName } });
        const userOrgMembersPromise = SunbitAPI.getUserOrgMembers({
          eventName: 'Fetch organization members',
          props: { userName },
        });

        await Promise.all([alliancePromise, userOrgMembersPromise]);

        const allianceResponse = await alliancePromise;
        const userOrgMembers = await userOrgMembersPromise;

        const allianceNewData: IAllianceData = {
          userFirstName: allianceResponse.firstName,
          userLastName: allianceResponse.lastName,
          allianceName: allianceResponse.allianceName,
          userRole: allianceResponse.role,
        };
        const formattedOrgMembers = userOrgMembers.members.map(({ firstName, lastName, role, email }) => ({
          name: `${firstName} ${lastName}`,
          role,
          email,
        }));

        appContext.setAllianceData(allianceNewData);
        appContext.setMembers(formattedOrgMembers);

        mixpanel.track('All User Details', appContext);
      } catch (error) {
      } finally {
        loaderContext.setLoader(false);
      }
    };

    getUserDetails();
    // eslint-disable-next-line
  }, [userName]);

  const addMembers = async () => {
    try {
      loaderContext.setLoader(true);
      appContext.cleanAddedMemberData();
      const newMember = {
        firstName: appContext.addMember.memberFirstName.value,
        lastName: appContext.addMember.memberLastName.value,
        email: appContext.addMember.email.value,
      };
      const response = await SunbitAPI.addMembers(newMember, {
        eventName: 'Add member',
        props: { newMember, userName },
      });
      const addedMember: IMembersData = {
        name: `${response.firstName} ${response.lastName}`,
        email: response.email,
        role: response.role,
      };

      appContext.setPopupMessage(addedMember.name, 'addMemberSuccess');
      appContext.setMembersList(addedMember);
    } catch (error) {
      appContext.setPopupMessage('Error', 'addMemberFail');
      appContext.setAddMembersPopup('addMemberFail');
    } finally {
      loaderContext.setLoader(false);
    }
  };

  const deleteAllData = async () => {
    try {
      loaderContext.setLoader(true);
      await SunbitAPI.deleteAllData();
      appContext.deleteAllData();
      appContext.setPopupMessage('Success', '');
    } catch (error) {
      appContext.setPopupMessage('Error', '');
      appContext.setDeleteAllDataPopup('');
    } finally {
      loaderContext.setLoader(false);
      appContext.setDeleteAllDataPopup('');
    }
  };

  const { privacyPolicyUrl, termsOfUseUrl, ratesAndTermsUrl } = CompanyUrls;
  const {
    members,
    addMember,
    membersPopup,
    setAddMembersPopup,
    deleteAllDataPopup,
    setDeleteAllDataPopup,
    allianceData,
    onFieldChange,
    testWebhookPopup,
  } = appContext;

  return (
    <IntegrationFlow
      {...{
        privacyPolicyUrl,
        termsOfUseUrl,
        ratesAndTermsUrl,
        name: `${allianceData.userFirstName} ${allianceData.userLastName}`,
        isOwner: allianceData.userRole === 'OWNER',
        userKeys: keys,
        webhooks,
        webhooksWaiting,
        setWebhook,
        updateWebhook,
        testWebhookUrl,
        keysWaiting,
        members,
        alliance: allianceData.allianceName,
        addMembersData: addMember,
        memberPopupData: membersPopup,
        setMemberPopup: setAddMembersPopup,
        addMembers,
        logout,
        onChange: onFieldChange,
        webhookPopupData: testWebhookPopup,
        setWebhookPopup: setTestWebhookPopup,
        prodRequestStatus,
        onRequestProdAccess,
        environmentsWaiting,
        deleteAllDataPopupData: deleteAllDataPopup,
        deleteAllData: deleteAllData,
        setDeleteAllDataPopup: setDeleteAllDataPopup,
      }}
    />
  );
};

export default MainLayoutContainer;
